import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Loader2, Zap, ZapOff, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { PageContainer } from "../components/kit";

// Which ATSes Autopilot may drive. These were twelve env vars until 2026-10-03; moving them here
// means an ATS that starts failing can be switched off in seconds instead of waiting for a deploy.
//
// Turning one OFF is not destructive and nothing is lost mid-flight: jobs on that ATS stop showing
// ⚡ and become ordinary "Apply" links to the employer's own site, and applications already approved
// keep the lane they were built with. Turning it back on restores ⚡ on the next grid refresh.
//
// Every change is written to an audit log, because this screen is now the ONLY record of such a
// decision — there is no env var in a reviewed diff any more.
export default function AdminAtsAutomation() {
  const [rows, setRows] = useState(null);
  const [refreshSecs, setRefreshSecs] = useState(60);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/ats-automation")
      .then(({ data }) => { setRows(data.ats || []); setRefreshSecs(data.refresh_seconds || 60); })
      .catch(() => { setRows([]); toast.error("Couldn't load the ATS list."); });
  }, []);
  useEffect(load, [load]);

  const toggle = async (row) => {
    const next = !row.enabled;
    setBusy(row.ats);
    // Optimistic: the switch moves immediately, and reverts if the save fails.
    setRows((rs) => rs.map((r) => (r.ats === row.ats ? { ...r, enabled: next } : r)));
    try {
      await api.put(`/admin/ats-automation/${row.ats}`, { enabled: next });
      toast.success(`${row.label} ${next ? "enabled" : "disabled"}`);
      load();
    } catch {
      setRows((rs) => rs.map((r) => (r.ats === row.ats ? { ...r, enabled: row.enabled } : r)));
      toast.error(`Couldn't change ${row.label}.`);
    } finally { setBusy(null); }
  };

  const on = (rows || []).filter((r) => r.enabled).length;

  return (
    <PageContainer>
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#0F172A] mb-4">
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl text-[#0F172A]">ATS automation</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Which ATSes Autopilot may apply to. Switching one off turns its jobs into ordinary apply
            links — use it when an ATS starts failing.
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0025D8] hover:underline">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {rows === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-10">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mt-5 text-[13px] text-muted-foreground">
            <span className="font-semibold text-[#0F172A]">{on} of {rows.length}</span> enabled
            <span className="text-[#0F172A]/20">·</span>
            <span>changes reach other servers within {refreshSecs}s</span>
          </div>

          <ul className="mt-3 rounded-2xl border border-border divide-y divide-border overflow-hidden bg-white">
            {rows.map((r) => (
              <li key={r.ats} className="flex items-center gap-3 px-4 py-3">
                <span className={`w-8 h-8 rounded-lg grid place-items-center flex-shrink-0 ${
                  r.enabled ? "bg-[#0025D8]/[0.08] text-[#0025D8]" : "bg-[#0F172A]/[0.05] text-[#0F172A]/35"}`}>
                  {r.enabled ? <Zap className="w-4 h-4" /> : <ZapOff className="w-4 h-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-[#0F172A]">{r.label}</div>
                  <div className="text-[11.5px] text-muted-foreground truncate">
                    {r.updated_by && !String(r.updated_by).startsWith("seed:")
                      ? `${r.enabled ? "Enabled" : "Disabled"} by ${r.updated_by}${r.updated_at ? ` · ${r.updated_at.slice(0, 16).replace("T", " ")}` : ""}`
                      : "Never changed — still on its default"}
                  </div>
                </div>
                {r.enabled !== r.default && (
                  <span title={`Differs from the default in code (${r.default ? "on" : "off"})`}
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F59E0B]/10 text-[#B45309] flex-shrink-0">
                    <AlertTriangle className="w-3 h-3" /> overridden
                  </span>
                )}
                <button onClick={() => toggle(r)} disabled={busy === r.ats}
                  role="switch" aria-checked={r.enabled} aria-label={`${r.label} automation`}
                  className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${
                    r.enabled ? "bg-[#0025D8]" : "bg-[#0F172A]/15"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
                    r.enabled ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </li>
            ))}
          </ul>

          <p className="text-[11.5px] text-muted-foreground mt-3 leading-relaxed">
            Turning an ATS off does not cancel anything already approved — those keep the lane they
            were built with. Its jobs simply stop offering one-click apply until it is switched back on.
          </p>
        </>
      )}
    </PageContainer>
  );
}
